import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select, Table, Tag, message } from 'antd';
import { FaEye, FaUpload, FaBan, FaEyeSlash, FaSyncAlt, FaTags, FaUndo } from 'react-icons/fa';
import AppContext from '../contexts/AppContext';
import { IMPORTED_EVENT_CATEGORIES } from '../constants/importedEventCategories';

// Which venues support Imported Events at all, and whether each gets its
// events from an automatic scraper (-> "Publish All" button) or manual
// local-JSON sync (-> "Sync" button) now comes from the backend's
// SUPPORTED_IMPORTED_EVENT_VENUES config (see importedEventSupportedVenues
// in AppContext, fetched from GET /ts/imported-events/supported-venues) -
// this used to be three separate hardcoded lists here that could drift out
// of sync with each other and with the backend (which is exactly what
// happened when Yarrawonga was added).
const actionButtonClassName = '!h-10 !w-[133px] !justify-center !rounded-[22px] !px-2 !text-[15px] !font-semibold';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'imported', label: 'Imported' },
  { value: 'published', label: 'Published' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'hidden', label: 'Hidden' },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All Categories' },
  ...IMPORTED_EVENT_CATEGORIES.map((category) => ({ value: category, label: category })),
];

const IMPORT_METHOD_LABELS = {
  scheduled_scraper: 'Scheduled scraper',
  local_json: 'Local JSON',
  manual_url: 'Manual URL',
  listing_url: 'Listing Page',
};

const badgeColor = (status) => {
  switch (status) {
    case 'published':
      return 'green';
    case 'rejected':
      return 'red';
    case 'expired':
      return 'orange';
    case 'hidden':
      return 'default';
    default:
      return 'blue';
  }
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};


const getSourceKey = (item) => item?.sourcePageUrl || item?.sourceLabel || item?.importMethod || '';

const getSourceLabel = (item) => item?.sourceLabel || IMPORT_METHOD_LABELS[item?.importMethod] || item?.importMethod || '-';
const ImportedEvents = () => {
  const { token, selectedRole, importedEventSupportedVenues, importedEventSupportedVenuesLoaded } = useContext(AppContext);
  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [syncingLocal, setSyncingLocal] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [categoryEditItem, setCategoryEditItem] = useState(null);
  const [categoryEditSelection, setCategoryEditSelection] = useState([]);
  const [savingCategories, setSavingCategories] = useState(false);
  const [reviewRejectItem, setReviewRejectItem] = useState(null);
  const [submittingReviewReject, setSubmittingReviewReject] = useState(false);
  const [eventSources, setEventSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);

  const selectedVenueId = selectedRole?.id || '';
  const supportedVenues = useMemo(() => importedEventSupportedVenues || [], [importedEventSupportedVenues]);
  const selectedVenueConfig = supportedVenues.find((venue) => venue.venueId === selectedVenueId);
  const isSupportedVenue = Boolean(selectedVenueConfig);
  const canBulkPublish = selectedVenueConfig?.importMethod === 'scheduled_scraper' || selectedVenueConfig?.importMethod === 'listing_url';
  const canLocalSync = selectedVenueConfig?.importMethod === 'local_json';
  const showEventSources = selectedVenueConfig?.importMethod === 'listing_url';

  const venueOptions = useMemo(
    () => supportedVenues.map((venue) => ({ value: venue.venueId, label: venue.label })),
    [supportedVenues]
  );

  const sourceFilterOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((item) => {
      const value = getSourceKey(item);
      if (!value || seen.has(value)) return;
      seen.set(value, { value, label: getSourceLabel(item) });
    });
    return [{ value: '', label: 'All Sources' }, ...Array.from(seen.values())];
  }, [items]);

  const visibleItems = useMemo(() => {
    return items.filter((item) => {
      const matchesCategory = !categoryFilter || (Array.isArray(item.categories) && item.categories.includes(categoryFilter));
      const matchesSource = !sourceFilter || getSourceKey(item) === sourceFilter;
      return matchesCategory && matchesSource;
    });
  }, [items, categoryFilter, sourceFilter]);
  const fetchItems = async () => {
    if (!token || !selectedVenueId || !isSupportedVenue) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ venueId: selectedVenueId });
      if (statusFilter) params.set('status', statusFilter);
      const response = await fetch(`${apiBase}/ts/imported-events?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load imported events');
      }
      setItems(data.items || []);
    } catch (error) {
      message.error(error.message || 'Failed to load imported events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedVenueId, statusFilter, isSupportedVenue]);

  const fetchEventSources = async () => {
    if (!token || !selectedVenueId || !isSupportedVenue) {
      setEventSources([]);
      return;
    }
    setLoadingSources(true);
    try {
      const params = new URLSearchParams({ venueId: selectedVenueId });
      const response = await fetch(`${apiBase}/ts/imported-events/sources?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load event sources');
      }
      setEventSources(data.items || []);
    } catch (error) {
      message.error(error.message || 'Failed to load event sources');
    } finally {
      setLoadingSources(false);
    }
  };

  useEffect(() => {
    fetchEventSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedVenueId, isSupportedVenue]);

  const openPreviewModal = (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  };

  const buildUpdatePayload = (item, overrides) => ({
    venueId: item.venueId,
    sourceType: item.sourceType,
    sourceUrl: item.sourceUrl,
    sourceExternalId: item.sourceExternalId,
    sourceLabel: item.sourceLabel,
    sourcePageUrl: item.sourcePageUrl,
    title: item.title,
    summary: item.summary,
    description: item.description,
    imageUrl: item.imageUrl,
    locationName: item.locationName,
    address: item.address,
    startAt: item.startAt,
    endAt: item.endAt,
    ...overrides,
  });

  const openCategoryEditor = (item) => {
    setCategoryEditItem(item);
    setCategoryEditSelection(Array.isArray(item.categories) ? item.categories : []);
  };

  const saveCategoryEdit = async () => {
    if (!categoryEditItem) return;
    setSavingCategories(true);
    try {
      const response = await fetch(`${apiBase}/ts/imported-events/${categoryEditItem.id}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(
          buildUpdatePayload(categoryEditItem, {
            categories: categoryEditSelection.length ? categoryEditSelection : ['Other'],
            categoriesOverriddenByUser: true,
          })
        ),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save categories');
      }
      message.success('Categories updated');
      setCategoryEditItem(null);
      await fetchItems();
    } catch (error) {
      message.error(error.message || 'Failed to save categories');
    } finally {
      setSavingCategories(false);
    }
  };

  const resetToAutoCategories = async (item) => {
    try {
      const response = await fetch(`${apiBase}/ts/imported-events/${item.id}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buildUpdatePayload(item, { categoriesOverriddenByUser: false })),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset categories');
      }
      message.success('Categories reset to automatic');
      await fetchItems();
    } catch (error) {
      message.error(error.message || 'Failed to reset categories');
    }
  };

  const runAction = async (id, action, successMessage, body) => {
    try {
      const response = await fetch(`${apiBase}/ts/imported-events/${id}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Failed to ${action} imported event`);
      }
      message.success(successMessage);
      await fetchItems();
      return true;
    } catch (error) {
      message.error(error.message || `Failed to ${action} imported event`);
      return false;
    }
  };

  const openReviewReject = (item) => {
    setReviewRejectItem(item);
  };

  const submitReviewReject = async () => {
    if (!reviewRejectItem) return;
    setSubmittingReviewReject(true);
    try {
      const succeeded = await runAction(reviewRejectItem.id, 'reject', 'Event rejected');
      // Only close on success - runAction already surfaced the error via
      // message.error, but closing here regardless would also silently
      // discard whatever reason the user just typed, forcing them to
      // reopen the modal and retype it from scratch.
      if (succeeded) {
        setReviewRejectItem(null);
      }
    } finally {
      setSubmittingReviewReject(false);
    }
  };

  const publishAllImportedEvents = () => {
    if (!selectedVenueId) return;

    Modal.confirm({
      title: 'Publish all imported events?',
      content:
        'This will publish every imported event for this venue. Hidden, rejected and expired events will be skipped.',
      okText: 'Publish All',
      cancelText: 'Cancel',
      onOk: async () => {
        setPublishingAll(true);
        try {
          const response = await fetch(`${apiBase}/ts/imported-events/publish-all`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ venueId: selectedVenueId }),
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || 'Failed to publish imported events');
          }

          const result = data.result || {};
          const summary = [
            `published ${result.published || 0}`,
            `expired ${result.expired || 0}`,
            `failed ${result.failed || 0}`,
          ].join(', ');
          message.success(`Bulk publish complete: ${summary}`);
          await fetchItems();
        } catch (error) {
          message.error(error.message || 'Failed to publish imported events');
        } finally {
          setPublishingAll(false);
        }
      },
    });
  };

  const syncLocalSource = async () => {
    if (!selectedVenueId) return;

    setSyncingLocal(true);
    try {
      const response = await fetch(`${apiBase}/ts/imported-events/sync-local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ venueId: selectedVenueId }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync local source');
      }
      message.success(`Local source synced: ${data.result.created} created, ${data.result.updated} updated`);
      await fetchItems();
    } catch (error) {
      message.error(error.message || 'Failed to sync local source');
    } finally {
      setSyncingLocal(false);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => (
        <div>
          <div className="font-semibold text-[#1b2b44]">{record.title}</div>
          <div className="text-[12px] text-[#72809b]">{record.venueLabel}</div>
        </div>
      ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_, record) => (
        <div className="text-[12px] text-[#4d5b75]">
          <div>{formatDateTime(record.startAt)}</div>
          <div className="text-[#8b97af]">to {formatDateTime(record.endAt)}</div>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <div className="text-[12px] text-[#4d5b75]">
          <div>{record.locationName || '-'}</div>
          <div className="text-[#8b97af]">{record.address || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Source',
      key: 'source',
      render: (_, record) => (
        <div className="text-[12px] text-[#4d5b75]">
          <div>{getSourceLabel(record)}</div>
          {record.extractionStatus && record.extractionStatus !== 'full' && (
            <div className="text-[#c07a1b]">extraction: {record.extractionStatus}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Categories',
      key: 'categories',
      render: (_, record) => (
        <div className="flex max-w-[220px] flex-wrap gap-1">
          {(record.categories || []).map((category) => (
            <Tag key={category}>{category}</Tag>
          ))}
          {record.categoriesOverriddenByUser && (
            <Tag color="purple">manual</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag color={badgeColor(value)}>{String(value || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button className={actionButtonClassName} icon={<FaEye />} onClick={() => openPreviewModal(record)}>
            Preview
          </Button>
          <Button className={actionButtonClassName} icon={<FaTags />} onClick={() => openCategoryEditor(record)}>
            Categories
          </Button>
          {record.categoriesOverriddenByUser && (
            <Button className={actionButtonClassName} icon={<FaUndo />} onClick={() => resetToAutoCategories(record)}>
              Reset Auto
            </Button>
          )}
          {record.status !== 'rejected' && record.status !== 'hidden' && (
            <Button className={actionButtonClassName} type="primary" icon={<FaUpload />} onClick={() => runAction(record.id, 'publish', 'Published to touchscreen')}>
              Publish
            </Button>
          )}
          {record.status === 'imported' && (
            <Button className={actionButtonClassName} danger icon={<FaBan />} onClick={() => openReviewReject(record)}>
              Reject
            </Button>
          )}
          {record.status === 'published' && (
            <Button className={actionButtonClassName} icon={<FaEyeSlash />} onClick={() => runAction(record.id, 'hide', 'Published event hidden')}>
              Hide
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Don't render the "not supported" message until the venue config has
  // actually loaded - otherwise every venue (including ones that DO
  // support imported events) would briefly see it on first render, before
  // importedEventSupportedVenues has had a chance to arrive.
  if (!importedEventSupportedVenuesLoaded) {
    return null;
  }

  if (!isSupportedVenue) {
    const enabledVenueNames = venueOptions.map((venue) => venue.label).join(', ');
    return (
      <div className="mx-auto mb-20 w-[92%] max-w-[1380px] py-2">
        <div className="rounded-[28px] border border-[#e2e7f6] bg-white p-8 shadow-[0_16px_36px_rgba(15,23,42,0.05)]">
          <h2 className="text-xl font-semibold text-[#1b2b44]">Imported Events</h2>
          <p className="mt-3 max-w-2xl text-[14px] leading-7 text-[#617089]">
            {enabledVenueNames
              ? `Imported events are currently enabled for ${enabledVenueNames} touchscreen venues. Switch to one of those venue roles to manage this workflow.`
              : 'Imported events are not enabled for any touchscreen venue yet.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mb-20 w-[92%] max-w-[1380px] py-2">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="max-w-3xl text-[13px] leading-7 text-[#5f6f89]">
            Imported events are automatically pulled from the configured source links each day at 12:00 AM, then listed here for you to publish individually or in bulk to the touchscreen.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {canBulkPublish && (
            <Button
              type="primary"
              icon={<FaUpload />}
              loading={publishingAll}
              className="!mt-1 !h-10 !rounded-xl !px-5 !font-semibold"
              onClick={publishAllImportedEvents}
            >
              Publish All Imported
            </Button>
          )}
          {canLocalSync && (
            <Button
              icon={<FaSyncAlt />}
              loading={syncingLocal}
              className="!mt-1 !h-10 !rounded-xl !border-[#d8def2] !px-5 !font-semibold !text-[#1b2b44]"
              onClick={syncLocalSource}
            >
              Sync Local Source
            </Button>
          )}
        </div>
      </div>

      {showEventSources && (
        <div className="mb-4 rounded-[28px] border border-[#e2e7f6] bg-white p-4 shadow-[0_16px_36px_rgba(15,23,42,0.05)]">
          <h3 className="text-[15px] font-semibold text-[#1b2b44]">Event Sources</h3>
          <p className="mt-1 text-[12px] text-[#72809b]">
            Each source is an events listing page configured by admin. The backend checks these links automatically every day at 12:00 AM and adds any newly found events to the list below.
          </p>
          <div className="mt-3 space-y-2">
            {eventSources.map((source) => (
              <div
                key={source.id}
                className="flex flex-col gap-2 rounded-xl border border-[#e2e7f6] p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-[#1b2b44]">{source.label}</div>
                  <div className="truncate text-[12px] text-[#72809b]">{source.url}</div>
                  <div className="text-[11px] text-[#94a1b8]">
                    {source.lastImportedAt ? `Last imported ${formatDateTime(source.lastImportedAt)}` : 'Never imported'}
                    {source.lastImportResult?.error && (
                      <span className="ml-2 text-[#d4380d]">Last attempt failed: {source.lastImportResult.error}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!loadingSources && eventSources.length === 0 && (
              <div className="text-[13px] text-[#94a1b8]">No source links configured yet - ask a staff admin to add one.</div>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={selectedVenueId} options={venueOptions} disabled className="!w-[260px]" />
        <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} className="!w-[220px]" />
        <Select value={sourceFilter} onChange={setSourceFilter} options={sourceFilterOptions} className="!w-[220px]" />
        <Select value={categoryFilter} onChange={setCategoryFilter} options={CATEGORY_FILTER_OPTIONS} className="!w-[220px]" />
      </div>

      <div className="rounded-[28px] border border-[#e2e7f6] bg-white p-4 shadow-[0_16px_36px_rgba(15,23,42,0.05)]">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={visibleItems}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <Modal title="Imported Event Preview" open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} width={760}>
        {previewItem && (
          <div className="space-y-3 pt-1 text-[14px] text-[#334154]">
            <div>
              <h3 className="text-xl font-semibold text-[#1b2b44]">{previewItem.title}</h3>
              <div className="mt-1"><Tag color={badgeColor(previewItem.status)}>{String(previewItem.status || '').toUpperCase()}</Tag></div>
            </div>
            <div><strong>Venue:</strong> {previewItem.venueLabel}</div>
            <div><strong>Time:</strong> {formatDateTime(previewItem.startAt)} to {formatDateTime(previewItem.endAt)}</div>
            <div><strong>Location:</strong> {[previewItem.locationName, previewItem.address].filter(Boolean).join(', ') || '-'}</div>
            {previewItem.imageUrl && (
              <div>
                <img src={previewItem.imageUrl} alt={previewItem.title} className="max-h-[220px] rounded-xl border border-[#e2e7f6] object-cover" />
              </div>
            )}
            {previewItem.summary && <div><strong>Summary:</strong> {previewItem.summary}</div>}
            {previewItem.description && <div><strong>Description:</strong><p className="mt-1 whitespace-pre-wrap">{previewItem.description}</p></div>}
            {previewItem.sourceUrl && (
              <div>
                <strong>Source:</strong>{' '}
                <a href={previewItem.sourceUrl} target="_blank" rel="noreferrer" className="text-[#4a5cff] underline">
                  {previewItem.sourceUrl}
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Edit Categories"
        open={Boolean(categoryEditItem)}
        onCancel={() => setCategoryEditItem(null)}
        onOk={saveCategoryEdit}
        confirmLoading={savingCategories}
        okText="Save"
      >
        {categoryEditItem && (
          <div className="space-y-3 pt-1">
            <div className="text-[14px] font-semibold text-[#1b2b44]">{categoryEditItem.title}</div>
            <p className="text-[12px] text-[#72809b]">
              Categories are picked automatically from the title and summary. Saving here marks them as manually set,
              so future re-imports won't overwrite your choice - use "Reset Auto" on the list to hand it back to the
              automatic classifier.
            </p>
            <Select
              mode="multiple"
              className="!w-full"
              value={categoryEditSelection}
              onChange={setCategoryEditSelection}
              options={IMPORTED_EVENT_CATEGORIES.map((category) => ({ value: category, label: category }))}
              placeholder="Select one or more categories"
            />
          </div>
        )}
      </Modal>

      <Modal
        title="Reject Event"
        open={Boolean(reviewRejectItem)}
        onCancel={() => setReviewRejectItem(null)}
        onOk={submitReviewReject}
        confirmLoading={submittingReviewReject}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        {reviewRejectItem && (
          <div className="space-y-3 pt-1">
            <div className="text-[14px] font-semibold text-[#1b2b44]">{reviewRejectItem.title}</div>
            <div>
            <p className="text-[12px] text-[#72809b]">
              This will mark the event as rejected and keep it off the touchscreen until it is re-imported or updated.
            </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ImportedEvents;





